import { FC, useState } from 'react';
import {
  appointment_package,
  appointment_product,
  appointment_service,
  appointmentDetails,
  availableTeamMember,
} from '../../../../types/calendar';
import { useIntl } from 'react-intl';
import { primarySelectedIconSVG } from '../../../../assets/SVGs/SVGs';

import Service from './Service';
import Package from './Package';
import Product from './Product';
import { useQuery } from '@tanstack/react-query';
import {
  adminGetAvailableTeamMembers,
  adminGetSalonPaymentMethods,
} from '../../../../network/admin';
import dayjs from 'dayjs';
import { Modal } from 'antd';
import ChangePaymentMethod from './ChangePaymentMethod';
import { getPaymentMethods } from '../../../../network/settings';
import { getAvailableTeamMembersByServices } from '../../../../network/teamMember';
import LoyaltyDiscount from '../addAppointment/LoyaltyDiscount';

interface AppointmentServicesDetailsProps {
  services: appointment_service[];
  packages: appointment_package[];
  products: appointment_product[];
  loyaltyDiscount?: { discount_percentage: string; id: number };
  paymentMethod: { id: number; name: string } | null;
  setPaymentMethod: React.Dispatch<
    React.SetStateAction<{
      id: number;
      name: string;
    } | null>
  >;
  changeEditMode: (
    id: number,
    value: boolean,
    type: 'service' | 'package' | 'product'
  ) => void;
  changeTeamMember: (
    id: number,
    teamMember: availableTeamMember,
    type: 'service' | 'package'
  ) => void;
  deleteService: (index: number) => void;
  deletePackage: (index: number) => void;
  deleteProduct: (id: number) => void;
  handleChangeProductQty: (
    productId: number,
    type: 'increase' | 'decrease'
  ) => void;
  appointment: appointmentDetails;
  isAdmin?: boolean;
}

const AppointmentServicesDetails: FC<AppointmentServicesDetailsProps> = ({
  packages,
  products,
  services,
  changeEditMode,
  changeTeamMember,
  deletePackage,
  deleteService,
  deleteProduct,
  handleChangeProductQty,
  appointment,
  isAdmin,
  paymentMethod,
  setPaymentMethod,
  loyaltyDiscount,
}) => {
  const intl = useIntl();
  const [changePaymentMethodModal, setChangePaymentMethodModal] =
    useState(false);

  const canEdit = (): boolean => {
    if (dayjs(appointment?.date).isBefore(dayjs())) {
      return false;
    }
    if (
      appointment?.status === 'canceled' ||
      appointment?.status === 'completed'
    ) {
      return false;
    } else {
      return true;
    }
  };
  const { data: teamMembers } = useQuery(
    [
      'availableTeamMembers',
      appointment?.id,
      appointment?.start_time,
      appointment?.appointment_services,
      appointment?.appointment_packages,
    ],

    () => {
      if (canEdit()) {
        const startTime = appointment?.start_time?.split(':');
        if (isAdmin) {
          return adminGetAvailableTeamMembers({
            locationId: appointment?.location_id,
            date: dayjs(appointment?.date).format('YYYY-MM-DD'),
            appointment_id: appointment?.id,
            start_time: [startTime[0], startTime[1]].join(':'),
            pricing_option_ids: appointment?.appointment_services.map(
              (service) => service?.pricing_option?.id
            ),
            package_ids: appointment?.appointment_packages.map(
              (packageItem) => packageItem?.package?.id
            ),
            available_for: 'both',
            // available_for:
            //   clientPreferredGender === "female"
            //     ? "woman"
            //     : clientPreferredGender === "male"
            //     ? "man"
            //     : clientPreferredGender || "both",
          });
        } else {
          return getAvailableTeamMembersByServices({
            appointment_id: appointment?.id,
            locationId: appointment?.location_id,
            date: dayjs(appointment?.date).format('YYYY-MM-DD'),
            start_time: [startTime[0], startTime[1]].join(':'),
            pricing_option_ids: appointment?.appointment_services.map(
              (service) => service?.pricing_option?.id
            ),
            package_ids: appointment?.appointment_packages.map(
              (packageItem) => packageItem?.package?.id
            ),
            available_for: 'both',
            // available_for:
            //   clientPreferredGender === "female"
            //     ? "woman"
            //     : clientPreferredGender === "male"
            //     ? "man"
            //     : clientPreferredGender || "both",
          });
        }
      }
    },
    { select: (res) => res?.data }
  );
  const totalWithoutTax =
    appointment?.appointment_services?.reduce(
      (sum, service) => sum + Number(service?.pricing_option?.price),
      0
    ) +
    appointment?.appointment_packages?.reduce(
      (sum, pkg) => sum + Number(pkg?.package?.retail_price),
      0
    ) +
    appointment?.products?.reduce(
      (sum, product) => sum + Number(product?.retail_price),
      0
    );
  let fixedDiscount = 0;
  if (appointment?.discount && Number(appointment?.discount) > 0) {
    if (appointment?.discount_type === 'fixed') {
      fixedDiscount += +appointment?.discount;
    }
    if (appointment?.discount_type === 'percentage') {
      fixedDiscount += (totalWithoutTax * +appointment?.discount) / 100;
    }
  }
  let totalPriceWithDiscount = totalWithoutTax - fixedDiscount;
  let taxValue = appointment?.taxable
    ? Number(
        +totalPriceWithDiscount * (+appointment?.taxable?.tax_rate / 100)
      ).toFixed(2)
    : 0;
  return (
    <div className="space-y-2">
      <div className="">
        <div className="p-2 rounded  bg-[#FBFBFF]">
          {services?.map((service, index) => (
            <Service
              service={service}
              teamMembers={teamMembers || []}
              changeEditMode={changeEditMode}
              changeTeamMember={changeTeamMember}
              deleteService={deleteService}
              key={index}
              canEdit={canEdit()}
            />
          ))}

          {packages?.map((pkg, index) => (
            <Package
              package={pkg}
              teamMembers={teamMembers || []}
              changeEditMode={changeEditMode}
              changeTeamMember={changeTeamMember}
              deletePackage={deletePackage}
              key={index}
              canEdit={canEdit()}
            />
          ))}

          {products?.map((product, index) => (
            <Product
              product={product}
              key={index}
              changeEditMode={changeEditMode}
              deleteProduct={deleteProduct}
              handleChangeProductQty={handleChangeProductQty}
              canEdit={canEdit()}
            />
          ))}
        </div>
        {loyaltyDiscount ? (
          <LoyaltyDiscount
            loyaltyDiscount={{
              amount: totalWithoutTax,
              percentage: Number(loyaltyDiscount?.discount_percentage),
            }}
          />
        ) : null}
        <div className="p-2 rounded  bg-[#EEF0FF]">
          <div className="flex gap-2    p-2 justify-between rounded">
            <p className="flex gap-2 items-center">
              <span>{intl.formatMessage({ id: 'subTotal' })}</span>
            </p>

            <p className="flex gap-1 items-center ">
              <span className="text-color-primary font-semibold">
                {Number(totalWithoutTax).toFixed(2)}
              </span>
              <small className="text-xs self-center mt-2">
                {intl.formatMessage({
                  id: 'SARshort',
                  defaultMessage: 'ر.س',
                })}
              </small>
            </p>
          </div>
          {appointment?.discount && Number(appointment?.discount) > 0 ? (
            <div className="flex gap-2    p-2 justify-between rounded">
              <p className="flex gap-2 items-center">
                <span>{intl.formatMessage({ id: 'discount' })}</span>
              </p>

              <p className="flex gap-1 items-center ">
                <span className="text-color-primary font-semibold text-red-600">
                  -{fixedDiscount?.toFixed(2)}
                </span>
                <small className="text-xs self-center mt-2">
                  {intl.formatMessage({
                    id: 'SARshort',
                    defaultMessage: 'ر.س',
                  })}
                </small>
                {appointment?.discount_type === 'percentage' ? (
                  <span className="text-color-primary font-semibold text-red-600">
                    {'( '}
                    {Number(appointment?.discount).toFixed()}%{' )'}
                  </span>
                ) : null}
              </p>
            </div>
          ) : null}
          <div className="flex gap-2    p-2 justify-between rounded">
            <p className="flex gap-2 items-center">
              <span>{intl.formatMessage({ id: 'tax' })}</span>
            </p>

            <p className="flex gap-1 items-center ">
              <span className="text-color-primary font-semibold">
                {taxValue || 0}
              </span>
              <small className="text-xs self-center mt-2">
                {intl.formatMessage({
                  id: 'SARshort',
                  defaultMessage: 'ر.س',
                })}
              </small>
              {appointment?.taxable ? (
                <span className="">
                  {'( ' +
                    Number(appointment?.taxable?.tax_rate).toFixed() +
                    '% )'}
                </span>
              ) : null}
            </p>
          </div>

          <div className="w-11/12 mx-auto bg-slate-300 px-10 h-[1px]" />

          <div className="flex gap-2   p-2 justify-between rounded">
            <p className="flex gap-2 items-center">
              <span className="">
                {intl.formatMessage({
                  id: 'totalPrice',
                  defaultMessage: 'الحساب الكلي',
                })}
              </span>
            </p>

            <p className="flex gap-1 items-center ">
              <span className="text-color-primary font-semibold">
                {Number(appointment?.cost).toFixed(2)}
              </span>
              <small className="text-xs self-center mt-2">
                {' '}
                {intl.formatMessage({
                  id: 'SARshort',
                  defaultMessage: 'ر.س',
                })}
              </small>
            </p>
          </div>
          <div className="flex gap-2   p-2 justify-between rounded">
            <p className="flex gap-2 items-center">
              <span className="">
                {intl.formatMessage({
                  id: 'payWith',
                  defaultMessage: 'الدفع من خلال',
                })}
              </span>
            </p>

            <p className="flex gap-1 items-center h-[34px] ">
              {paymentMethod ? (
                <span className="text-color-primary font-semibold self-end">
                  {primarySelectedIconSVG}
                </span>
              ) : null}
              <span className="text-color-primary font-semibold">
                {paymentMethod?.name}
              </span>
              {canEdit() ? (
                <span
                  className="text-[#5F72FF] cursor-pointer"
                  onClick={() => setChangePaymentMethodModal(true)}
                >
                  {' ( '} {intl.formatMessage({ id: 'change' })} {')'}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </div>
      {canEdit() ? (
        <Modal
          width={600}
          open={changePaymentMethodModal}
          footer={null}
          onCancel={() => setChangePaymentMethodModal(false)}
          centered
          destroyOnClose
        >
          <ChangePaymentMethod
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            getPaymentMethods={
              isAdmin
                ? () =>
                    adminGetSalonPaymentMethods(appointment?.location?.salon_id)
                : getPaymentMethods
            }
            onCancel={() => setChangePaymentMethodModal(false)}
            isAdmin={isAdmin}
          />
        </Modal>
      ) : null}
    </div>
  );
};

export default AppointmentServicesDetails;
